import React, { useMemo } from 'react'
import { Box, Stack, Typography, Paper, Tooltip, Chip } from '@mui/material'
import moment from 'moment'

const SIDEBAR_WIDTH = 260
const ROW_HEIGHT = 72
const HEADER_HEIGHT = 56
const ITEM_TOP = 12
const ITEM_HEIGHT = 46
const SINGLE_DAY_GUTTER_X = 5
const MULTI_DAY_GUTTER_X = 5

function defaultGetItemColor(item) {
  switch (item.type) {
    case 'booking':
      return 'primary.main'
    case 'maintenance':
      return 'warning.main'
    case 'defect':
      return 'error.main'
    case 'reserved':
      return 'secondary.main'
    default:
      return 'grey.500'
  }
}

function defaultGetItemLabel(item) {
  return item.label || item.type || 'Unavailable'
}

function defaultGetTooltipContent(item) {
  return (
    <Stack spacing={0.5}>
      <Typography variant="subtitle2" fontWeight={700}>
        {item.label || 'Unavailable'}
      </Typography>

      {item.type && (
        <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
          {item.type}
        </Typography>
      )}

      <Typography variant="caption">
        {moment(item.start).format('ddd D MMM YYYY, HH:mm')}
      </Typography>
      <Typography variant="caption">
        to {moment(item.end).format('ddd D MMM YYYY, HH:mm')}
      </Typography>
    </Stack>
  )
}

function getDaySpan(itemStart, itemEnd, rangeStart, rangeEnd) {
  const visibleStart = moment.max(moment(itemStart), moment(rangeStart))
  const visibleEnd = moment.min(moment(itemEnd), moment(rangeEnd))

  if (!visibleEnd.isAfter(visibleStart)) {
    return null
  }

  const startDay = visibleStart.clone().startOf('day')
  const endDay = visibleEnd.clone().subtract(1, 'millisecond').startOf('day')
  const rangeDayStart = moment(rangeStart).startOf('day')

  const startIndex = startDay.diff(rangeDayStart, 'days')
  const endIndex = endDay.diff(rangeDayStart, 'days')

  return {
    startIndex,
    endIndex,
    dayCount: endIndex - startIndex + 1,
  }
}

export default function ResourceAvailabilityTimeline({
  resources = [],
  items = [],
  rangeStart,
  rangeEnd,
  renderResource,
  getItemColor = defaultGetItemColor,
  getItemLabel = defaultGetItemLabel,
  getTooltipContent = defaultGetTooltipContent,
  onItemClick,
  onRowClick,
}) {
  const startMs = moment(rangeStart).startOf('day').valueOf()
  const endMs = moment(rangeEnd).startOf('day').valueOf()

  const start = useMemo(() => moment(startMs), [startMs])
  const end = useMemo(() => moment(endMs), [endMs])

  const dayColumns = useMemo(() => {
    const days = []
    const cursor = moment(startMs).startOf('day')
    const finish = moment(endMs).startOf('day')

    while (cursor.isBefore(finish)) {
      days.push(cursor.clone())
      cursor.add(1, 'day')
    }

    return days
  }, [startMs, endMs])

  const itemsByResource = useMemo(() => {
    const map = new Map()

    for (const resource of resources) {
      map.set(resource.id, [])
    }

    for (const item of items) {
      if (!map.has(item.resourceId)) {
        map.set(item.resourceId, [])
      }
      map.get(item.resourceId).push(item)
    }

    for (const [, resourceItems] of map) {
      resourceItems.sort(
        (a, b) => moment(a.start).valueOf() - moment(b.start).valueOf(),
      )
    }

    return map
  }, [resources, items])

  return (
    <Paper
      elevation={0}
      sx={{
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 3,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ minWidth: 1100 }}>
          <Box
            sx={{
              display: 'flex',
              height: HEADER_HEIGHT,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Box
              sx={{
                width: SIDEBAR_WIDTH,
                flexShrink: 0,
                borderRight: 1,
                borderColor: 'divider',
                px: 2,
                display: 'flex',
                alignItems: 'center',
                bgcolor: 'background.default',
              }}
            >
              <Typography variant="subtitle2">Vehicle</Typography>
            </Box>

            <Box sx={{ position: 'relative', flex: 1, display: 'flex' }}>
              {dayColumns.map((day) => (
                <Box
                  key={day.toISOString()}
                  sx={{
                    flex: 1,
                    borderRight: 1,
                    borderColor: 'divider',
                    px: 1.5,
                    py: 1,
                    bgcolor: 'background.default',
                    '&:last-child': { borderRight: 'none' },
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {day.format('ddd')}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {day.format('D MMM')}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {resources.map((resource) => {
            const resourceItems = itemsByResource.get(resource.id) || []

            return (
              <Box
                key={resource.id}
                sx={{
                  display: 'flex',
                  minHeight: ROW_HEIGHT,
                  borderBottom: 1,
                  borderColor: 'divider',
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <Box
                  onClick={() => onRowClick?.(resource)}
                  sx={{
                    width: SIDEBAR_WIDTH,
                    flexShrink: 0,
                    borderRight: 1,
                    borderColor: 'divider',
                    px: 2,
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: 'background.paper',
                    cursor: onRowClick ? 'pointer' : 'default',
                  }}
                >
                  {renderResource ? (
                    renderResource(resource)
                  ) : (
                    <Stack spacing={0.25}>
                      <Typography variant="body2" fontWeight={700}>
                        {resource.title}
                      </Typography>
                      {resource.subtitle && (
                        <Typography variant="caption" color="text.secondary">
                          {resource.subtitle}
                        </Typography>
                      )}
                    </Stack>
                  )}
                </Box>

                <Box
                  sx={{
                    position: 'relative',
                    flex: 1,
                    minHeight: ROW_HEIGHT,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      pointerEvents: 'none',
                    }}
                  >
                    {dayColumns.map((day) => (
                      <Box
                        key={day.toISOString()}
                        sx={{
                          flex: 1,
                          borderRight: 1,
                          borderColor: 'divider',
                          '&:last-child': { borderRight: 'none' },
                        }}
                      />
                    ))}
                  </Box>

                  {resourceItems.map((item) => {
                    const span = getDaySpan(item.start, item.end, start, end)
                    if (!span) return null

                    const gutterX =
                      span.dayCount === 1
                        ? SINGLE_DAY_GUTTER_X
                        : MULTI_DAY_GUTTER_X

                    const leftPercent =
                      (span.startIndex / dayColumns.length) * 100
                    const widthPercent =
                      (span.dayCount / dayColumns.length) * 100

                    return (
                      <Tooltip
                        key={item.id}
                        title={getTooltipContent(item)}
                        arrow
                        placement="top"
                      >
                        <Box
                          onClick={() => onItemClick?.(item, resource)}
                          sx={{
                            position: 'absolute',
                            left: `calc(${leftPercent}% + ${gutterX}px)`,
                            width: `calc(${widthPercent}% - ${gutterX * 2}px)`,
                            top: ITEM_TOP,
                            height: ITEM_HEIGHT,
                            px: 1.5,
                            boxSizing: 'border-box',
                            borderRadius: 1.5,
                            bgcolor: getItemColor(item),
                            color: 'common.white',
                            display: 'flex',
                            alignItems: 'center',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            cursor: onItemClick ? 'pointer' : 'default',
                            boxShadow: 'none',
                            minWidth: 0,
                            zIndex: 2,
                          }}
                        >
                          <Typography
                            variant="body2"
                            noWrap
                            fontWeight={700}
                            color="#fff"
                          >
                            {getItemLabel(item)}
                          </Typography>
                        </Box>
                      </Tooltip>
                    )
                  })}
                </Box>
              </Box>
            )
          })}
        </Box>
      </Box>
    </Paper>
  )
}
